<template>
  <span
    class="dashboard-menu-item"
    :class="{
      'dashboard-menu-item--child': isChild,
      'dashboard-menu-item--icon-only': iconOnly,
    }"
  >
    <i
      v-if="showIcon"
      :class="['dashboard-menu-item__icon', iconClass]"
      aria-hidden="true"
    />
    <a-tooltip
      placement="top"
      :title="tooltipTitle"
      :mouse-enter-delay="0.25"
      :overlay-style="{ maxWidth: '280px' }"
    >
      <span class="dashboard-menu-item__label-wrap" @mouseenter="checkOverflow">
        <span ref="labelRef" class="dashboard-menu-item__label">{{ labelText }}</span>
      </span>
    </a-tooltip>
    <span
      v-if="badgeDisplay != null"
      class="dashboard-menu-item__badge"
      :title="badgeTitle"
    >{{ badgeDisplay }}</span>
  </span>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

interface Menu {
  id: number;
  linkTo: string;
  linkLabel: string;
  icon?: string;
  children?: Menu[];
  quantity?: number | string | null;
  badge?: number | string | null;
  badgeCount?: number | string | null;
  count?: number | string | null;
  pendingCount?: number | string | null;
}

/** Cap display at 3 digits — larger values show as "999+" */
const BADGE_MAX = 999;

/** Only positive finite numbers are shown; 0 / null / non-numeric → hidden */
const parseBadgeCount = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean" || typeof value === "object") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || !/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  return null;
};

const formatBadgeCount = (n: number): string =>
  n > BADGE_MAX ? `${BADGE_MAX}+` : String(Math.floor(n));

const props = withDefaults(
  defineProps<{
    item: Menu;
    showIcon?: boolean;
    iconOnly?: boolean;
    isChild?: boolean;
  }>(),
  { showIcon: true, iconOnly: false, isChild: false }
);

const { t, te } = useI18n();
const labelRef = ref<HTMLElement | null>(null);
const isOverflow = ref(false);

const labelText = computed(() => {
  const label = props.item.linkLabel?.trim() || "";
  if (!label) return "";
  if (te(label)) return t(label);
  const keyMatch = /^menu\.(.+)$/i.exec(label);
  if (keyMatch) {
    const shortKey = `menu.${keyMatch[1]}`;
    if (te(shortKey)) return t(shortKey);
    const raw = keyMatch[1].replace(/[-_]/g, " ");
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
  return label;
});

const tooltipTitle = computed(() => {
  if (props.iconOnly || !isOverflow.value) return "";
  return labelText.value;
});

const iconClass = computed(() => {
  const icon = props.item.icon?.trim();
  if (icon) return icon;
  return props.isChild ? "fa-solid fa-circle" : "fa-solid fa-folder";
});

const badgeRaw = computed(() =>
  parseBadgeCount(
    props.item.quantity ??
      props.item.badge ??
      props.item.badgeCount ??
      props.item.count ??
      props.item.pendingCount
  )
);

const badgeDisplay = computed(() => {
  const n = badgeRaw.value;
  return n == null ? null : formatBadgeCount(n);
});

const badgeTitle = computed(() => {
  const n = badgeRaw.value;
  if (n == null || n <= BADGE_MAX) return undefined;
  return String(Math.floor(n));
});

const checkOverflow = () => {
  const el = labelRef.value;
  if (!el) {
    isOverflow.value = false;
    return;
  }
  isOverflow.value = el.scrollWidth > el.clientWidth + 1;
};
</script>

<style scoped>
.dashboard-menu-item {
  display: inline-flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 10px;
  user-select: none;
}

.dashboard-menu-item--child {
  gap: 0;
}

.dashboard-menu-item--icon-only .dashboard-menu-item__label-wrap {
  display: none;
}

.dashboard-menu-item__label-wrap {
  flex: 1;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.dashboard-menu-item__label {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 40px;
}

.dashboard-menu-item__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-left: auto;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: color-mix(
    in srgb,
    var(--sidebar-text-color, var(--primary-gold, #b19663)) 22%,
    transparent
  );
  color: var(--sidebar-text-color, var(--primary-gold-light, #f4d03f));
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.01em;
  text-align: center;
}
</style>
